import { useQuery } from "@tanstack/react-query";
import {
  Ban,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  History,
  MessageSquare,
  MinusCircle,
  Printer,
  RefreshCcw,
  Send,
  X as XIcon,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";

import type { OrderPayment } from "@tilt/pos-sdk";

import { getPosClient, sendPaymentLink, sendPaymentReceipt } from "../../lib/api";
import { formatCents, formatDateTime, formatDateTimeWithYear } from "../../lib/format";
import {
  lookupMethodByTypeSubtype,
  resolveMethodIcon,
  resolveMethodLabel,
} from "../../lib/payment-methods";
import { isNonTerminalPaymentStatus } from "../../lib/paymentStatus";
import { useFocusTrap } from "../../lib/use-focus-trap";
import { useActiveTimezone } from "../../lib/useActiveTimezone";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { PaymentStatusBadge } from "../ui/StatusBadge";

/**
 * Side-drawer with the full details of a single payment row.
 *
 * The compact Timeline row only renders method · amount · status ·
 * time so it stays readable in the narrow right rail (~250-320px).
 * Everything else - external_ref, full date+time stamps, payment link,
 * cancellation reason / watch state, lifecycle audit history, and the
 * cancel action - lives here. Opens via a "Details" link on the row.
 *
 * Slides in from the right on lg+; on phones it covers the screen so
 * the full audit history fits without horizontal scrolling.
 */

const STATUS_ICONS: Record<string, ReactNode> = {
  APPROVED: <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden="true" />,
  SETTLED: <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden="true" />,
  PENDING: <Clock className="h-5 w-5 text-amber-500" aria-hidden="true" />,
  ACTION_REQUIRED: <Clock className="h-5 w-5 text-amber-500" aria-hidden="true" />,
  DECLINED: <XCircle className="h-5 w-5 text-red-500" aria-hidden="true" />,
  FAILED: <XCircle className="h-5 w-5 text-red-500" aria-hidden="true" />,
};

export interface TimelinePaymentDrawerProps {
  readonly payment: OrderPayment;
  readonly currency: string;
  readonly locationId?: string | null | undefined;
  readonly orderId: string;
  readonly customerPhone?: string | null | undefined;
  readonly onClose: () => void;
  readonly onCancelPayment?: ((paymentId: string) => void) | undefined;
  readonly onPrintReceipt?: (() => void) | undefined;
}

export function TimelinePaymentDrawer({
  payment: p,
  currency,
  locationId,
  orderId,
  customerPhone,
  onClose,
  onCancelPayment,
  onPrintReceipt,
}: TimelinePaymentDrawerProps) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const drawerRef = useFocusTrap<HTMLDivElement>(true, onClose);
  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = useMemo(() => railsQuery.data?.rails ?? [], [railsQuery.data]);
  const rail = lookupMethodByTypeSubtype(rails, p.payment_type, p.payment_subtype ?? null);

  // Esc closes - useFocusTrap already handles this, but it also exits
  // on click-outside. Backdrop click below dispatches the same close.

  const isCancelled = !!p.cancelled_at;
  const isWatchTerminated = !!p.polling_terminated_at;
  const canCancel =
    onCancelPayment !== undefined &&
    p.role === "charge" &&
    isNonTerminalPaymentStatus(p.status) &&
    !isCancelled;

  // Lock background scroll while the drawer is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    // ``top-14`` parks the drawer below the global topbar (h-14) so
    // its own header - and the X close button - never disappears
    // behind the brand strip. Prior ``inset-0`` placement put both
    // headers at y=0 and even though the drawer's z-index is
    // strictly higher, the user reported the drawer rendering "behind
    // the header" - a stacking-context regression triggered by the
    // post-redesign topbar (2026-05-03 dogfood).
    <div className="fixed inset-x-0 bottom-0 top-14 z-50 flex justify-end">
      {/* Backdrop is a button so jsx-a11y is happy with the click +
          keyboard story. Escape on the focus-trapped drawer also
          closes (useFocusTrap wires that for us). */}
      <button
        type="button"
        aria-label={t("common.cancel")}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/30"
        tabIndex={-1}
      />
      <aside
        ref={drawerRef}
        className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-surface shadow-elev-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-drawer-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-muted text-muted">
              {p.role === "refund" ? (
                <RefreshCcw className="h-5 w-5" aria-hidden="true" />
              ) : isCancelled ? (
                <Ban className="h-5 w-5" aria-hidden="true" />
              ) : (
                (() => {
                  const Icon = rail ? resolveMethodIcon(rail) : MinusCircle;
                  return <Icon className="h-5 w-5" aria-hidden />;
                })()
              )}
            </span>
            <div>
              <h2 id="payment-drawer-title" className="text-base font-semibold text-strong">
                {rail ? resolveMethodLabel(rail, t) : p.payment_type}
                {p.role === "refund" && (
                  <span className="ml-2 text-xs font-medium uppercase tracking-wider text-muted">
                    {t("sale.refund")}
                  </span>
                )}
              </h2>
              <p className="mt-0.5 text-xs text-muted">{formatDateTime(p.created_at, tz)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface-muted text-muted transition hover:bg-surface-strong hover:text-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <XIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-5 px-5 py-5">
          {/* Amount + status - the two facts the cashier most needs at
              a glance. Refunds render as negatives; cancelled rows
              strike through. */}
          <div className="flex items-center justify-between gap-3">
            <span
              className={cn(
                "text-2xl font-bold tabular-nums",
                p.role === "refund"
                  ? "text-red-600"
                  : isCancelled
                    ? "text-muted line-through"
                    : "text-strong",
              )}
            >
              {p.role === "refund" ? "−" : ""}
              {formatCents(p.applied_amount_cents, currency)}
            </span>
            <div className="flex items-center gap-2">
              {STATUS_ICONS[p.status]}
              <PaymentStatusBadge status={p.status} />
            </div>
          </div>

          {/* Surcharge - shown only when Valor collected a card surcharge. */}
          {p.role === "charge" && p.surcharge_cents > 0 && (
            <DetailRow label={t("sale.surcharge")}>
              <span className="tabular-nums text-sm">
                {formatCents(p.surcharge_cents, currency)}
              </span>
            </DetailRow>
          )}

          {/* Destructive action - sits immediately under the status row
              so it reads as "this PENDING charge → you can void it",
              not as a generic close affordance. (2026-05-03 dogfood:
              prior placement in the bottom footer with ghost text
              styling was misread as a close link.) Hidden when the
              row isn't cancellable. */}
          {canCancel && (
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Ban className="h-3.5 w-3.5" />}
              onClick={() => {
                onCancelPayment?.(p.payment_id);
                onClose();
              }}
            >
              {t("sale.timelineCancelPayment")}
            </Button>
          )}

          {/* V0080 - receipt actions: Print + Send SMS.
              Print calls back to the parent (OrderDetailPage) which
              opens the print dialog with the full order envelope.
              SMS send POSTs to the manual-send endpoint and shows
              the outcome inline (sent / skipped reason / error). */}
          <SendReceiptButton orderId={orderId} payment={p} onPrintReceipt={onPrintReceipt} />

          {/* External reference - the provider's id (Aeropay UUID,
              Valor merchant_ref, …). Useful for support lookups. */}
          {p.external_ref && (
            <DetailRow label={t("sale.timelineExternalRef")}>
              <span className="font-mono text-xs text-foreground">{p.external_ref}</span>
            </DetailRow>
          )}

          {/* Hosted payment link - for async rails (Aeropay /paymentLink,
              Valor pay-by-text). Non-terminal charges get the full
              QR + copy + SMS panel so the cashier can re-share the link
              if the original SMS was lost. Failed/voided/cancelled rows
              show the URL for reference. Approved/settled rows hide it. */}
          {p.payment_link &&
          !isCancelled &&
          p.role === "charge" &&
          isNonTerminalPaymentStatus(p.status) ? (
            <PaymentLinkSection
              url={p.payment_link}
              orderId={orderId}
              customerPhone={customerPhone}
            />
          ) : p.payment_link && !["APPROVED", "SETTLED"].includes(p.status) ? (
            <DetailRow label={t("sale.timelinePaymentLink")}>
              <a
                href={p.payment_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 break-all text-xs font-medium text-primary hover:underline"
              >
                {p.payment_link}
                <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
              </a>
            </DetailRow>
          ) : null}

          {/* Cancellation context - visible only on cancelled rows. */}
          {isCancelled && (
            <div className="rounded-lg bg-surface-muted px-4 py-3 text-xs">
              <div className="flex items-center gap-2 font-medium text-foreground">
                <Ban className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                <span>{t("sale.timelineCancelledAt")}</span>
                <span className="text-muted">{formatDateTimeWithYear(p.cancelled_at!, tz)}</span>
              </div>
              {p.cancellation_reason && (
                <p className="mt-1 italic text-muted">“{p.cancellation_reason}”</p>
              )}
              <p className="mt-2 text-[0.6875rem] text-faint">
                {isWatchTerminated
                  ? t("sale.timelinePollingStopped")
                  : t("sale.timelineStillWatching")}
              </p>
            </div>
          )}

          {/* Lifecycle history - full audit log of every event on this
              payment (cancellation, expiry, late-completion, …). The
              previous inline disclosure stacked the events under the
              row in the narrow right rail and squashed everything;
              moving it here gives each event line room to breathe. */}
          <PaymentHistorySection orderId={orderId} paymentId={p.payment_id} />
        </div>
      </aside>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function PaymentLinkSection({
  url,
  orderId,
  customerPhone,
}: {
  readonly url: string;
  readonly orderId: string;
  readonly customerPhone?: string | null | undefined;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetRef = useRef<number | null>(null);
  const [phone, setPhone] = useState(customerPhone ?? "");
  const [sendState, setSendState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const sentRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetRef.current) window.clearTimeout(resetRef.current);
      if (sentRef.current) window.clearTimeout(sentRef.current);
    };
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      if (resetRef.current) window.clearTimeout(resetRef.current);
      resetRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  async function handleSend() {
    const trimmed = phone.trim();
    if (!trimmed || sendState === "sending") return;
    setSendState("sending");
    try {
      const result = await sendPaymentLink(orderId, trimmed);
      if (result.sent) {
        setSendState("sent");
        sentRef.current = window.setTimeout(() => setSendState("idle"), 4000);
      } else {
        setSendState("error");
      }
    } catch {
      setSendState("error");
    }
  }

  return (
    <DetailRow label={t("paymentLink.title")}>
      <div className="mt-2 flex flex-col gap-3">
        <div className="flex items-start gap-4">
          <div className="shrink-0 rounded-lg bg-white p-2 shadow-elev-1">
            <QRCodeSVG value={url} size={96} marginSize={1} aria-label={t("paymentLink.qrAlt")} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all font-mono text-xs text-foreground underline-offset-2 hover:underline"
            >
              {url}
            </a>
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="inline-flex w-fit items-center gap-1.5 rounded-md bg-surface-muted px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
                  {t("paymentLink.copied")}
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("paymentLink.copy")}
                </>
              )}
            </button>
          </div>
        </div>

        <div className="border-t border-line pt-3">
          <div className="mb-1.5 flex items-center gap-1.5">
            <MessageSquare className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
            <span className="text-xs font-semibold text-foreground">
              {t("paymentLink.sendText")}
            </span>
          </div>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (sendState === "error") setSendState("idle");
              }}
              placeholder="+15555550100"
              disabled={sendState === "sending"}
              className={cn(
                "min-h-tap flex-1 rounded-lg border border-line bg-surface-muted px-3 py-2 text-sm text-foreground",
                "placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-primary/50",
                sendState === "error" && "border-rose-400 focus:ring-rose-400/50",
              )}
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!phone.trim() || sendState === "sending"}
              className={cn(
                "inline-flex min-h-tap items-center gap-1.5 rounded-lg px-3 text-xs font-medium transition",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                sendState === "sent"
                  ? "bg-emerald-600 text-white"
                  : sendState === "error"
                    ? "bg-rose-500 text-white"
                    : phone.trim()
                      ? "bg-primary text-primary-foreground hover:opacity-90"
                      : "cursor-not-allowed bg-surface-muted text-muted",
              )}
            >
              {sendState === "sending" ? (
                t("paymentLink.sending")
              ) : sendState === "sent" ? (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("paymentLink.sent")}
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("paymentLink.sendAction")}
                </>
              )}
            </button>
          </div>
          {sendState === "error" && (
            <p className="mt-1.5 text-xs text-rose-500" role="alert">
              {t("paymentLink.sendError")}
            </p>
          )}
        </div>
      </div>
    </DetailRow>
  );
}

function PaymentHistorySection({
  orderId,
  paymentId,
}: {
  readonly orderId: string;
  readonly paymentId: string;
}) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const events = useQuery({
    queryKey: ["payment-lifecycle", orderId, paymentId],
    queryFn: () => getPosClient().getPaymentLifecycle(orderId, paymentId),
    staleTime: 30_000,
  });

  if (events.isPending) {
    return (
      <DetailRow label={t("sale.timelineHistory")}>
        <p className="text-xs text-muted">{t("sale.timelineHistoryLoading")}</p>
      </DetailRow>
    );
  }
  if (events.isError) {
    return (
      <DetailRow label={t("sale.timelineHistory")}>
        <p className="text-xs text-red-600">{t("sale.timelineHistoryError")}</p>
      </DetailRow>
    );
  }
  const list = events.data?.events ?? [];
  if (list.length === 0) {
    return (
      <DetailRow label={t("sale.timelineHistory")}>
        <p className="text-xs italic text-faint">{t("sale.timelineHistoryEmpty")}</p>
      </DetailRow>
    );
  }
  return (
    <DetailRow label={t("sale.timelineHistory")}>
      <ol className="space-y-2.5 border-l border-line pl-3">
        {list.map((ev) => (
          <li key={ev.id} className="flex items-start gap-2 text-xs">
            <History className="mt-0.5 h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-foreground">{kindLabel(ev.kind, t)}</span>
                <span className="text-[0.6875rem] text-muted">
                  {formatDateTimeWithYear(ev.created_at, tz)}
                </span>
              </div>
              {ev.reason && <p className="mt-0.5 italic text-muted">“{ev.reason}”</p>}
            </div>
          </li>
        ))}
      </ol>
    </DetailRow>
  );
}

function kindLabel(kind: string, t: (k: string) => string): string {
  const known = ["cancelled", "expired", "vendor_late_completion", "watch_terminated"];
  if (known.includes(kind)) {
    return t(`sale.timelineHistoryKind.${kind}`);
  }
  return kind;
}

/**
 * V0080 - per-payment "Send SMS receipt" button.
 *
 * Resolves the receipt kind from the payment's role (charge → charge,
 * refund → refund), POSTs to the new manual-send endpoint, and surfaces
 * the dispatcher's outcome inline:
 *   * success → "Sent ✓" toast for 3s
 *   * skipped (no_recipient / no_binding / etc.) → reason inline
 *   * vendor_error → red text + reason
 *
 * No phone-edit field in this iteration - the customer phone on the
 * order is the source of truth. A follow-up wave can add a "Send to a
 * different number" affordance.
 */
function SendReceiptButton({
  orderId,
  payment,
  onPrintReceipt,
}: {
  readonly orderId: string;
  readonly payment: OrderPayment;
  readonly onPrintReceipt?: (() => void) | undefined;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    tone: "success" | "info" | "error";
    message: string;
  } | null>(null);

  // Track the latest auto-clear timer so a re-click within 3s cancels
  // the older fire - without this the previous timer would clear the
  // newer toast right after it appears.
  const clearTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (clearTimerRef.current != null) {
        window.clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  const kind: "charge" | "refund" = payment.role === "refund" ? "refund" : "charge";
  const targetId = kind === "refund" ? payment.payment_id : orderId;

  async function onSend() {
    setSubmitting(true);
    setFeedback(null);
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    try {
      const result = await sendPaymentReceipt(orderId, {
        kind,
        target_id: targetId,
      });
      if (result.sent) {
        setFeedback({ tone: "success", message: "Receipt sent" });
      } else if (result.skipped_reason === "vendor_error") {
        setFeedback({
          tone: "error",
          message: "Carrier rejected the send. Try again later.",
        });
      } else {
        setFeedback({
          tone: "info",
          message: humaniseSkip(result.skipped_reason),
        });
      }
    } catch (err) {
      setFeedback({
        tone: "error",
        message: err instanceof Error ? err.message : "Send failed",
      });
    } finally {
      setSubmitting(false);
      // Auto-clear so the drawer doesn't keep showing the last result.
      // The timer id is captured in the ref so a second click clears
      // the old timer before scheduling the new one - otherwise a
      // stale timer can fire AFTER the next request and wipe the
      // fresh feedback line.
      clearTimerRef.current = window.setTimeout(() => {
        setFeedback(null);
        clearTimerRef.current = null;
      }, 3000);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-2">
        {onPrintReceipt && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Printer className="h-3.5 w-3.5" />}
            onClick={onPrintReceipt}
          >
            Print receipt
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Send className="h-3.5 w-3.5" />}
          onClick={onSend}
          disabled={submitting}
        >
          {submitting ? "Sending…" : "Send SMS receipt"}
        </Button>
      </div>
      {feedback && (
        <p
          className={cn(
            "text-[11px]",
            feedback.tone === "success" && "text-emerald-600",
            feedback.tone === "info" && "text-muted",
            feedback.tone === "error" && "text-red-600",
          )}
        >
          {feedback.message}
        </p>
      )}
    </div>
  );
}

function humaniseSkip(reason: string): string {
  switch (reason) {
    case "no_recipient":
      return "No customer phone on file.";
    case "no_binding":
      return "SMS provider not yet configured for this location.";
    case "no_credential":
      return "Twilio credentials missing - ask an admin.";
    case "auto_send_disabled":
      return "Auto-send is off; manual send still works (this hit the gate path).";
    case "not_found":
      return "Order or payment not found.";
    case "":
      return "Sent (no carrier confirmation yet).";
    default:
      return `Skipped: ${reason}`;
  }
}
